import React, { useMemo, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Accordion, AccordionSummary, AccordionDetails,
  Chip, IconButton, Typography, Box, TextField, InputAdornment,
  FormControl, InputLabel, Select, MenuItem, Tooltip, Stack
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon, Edit as EditIcon, Delete as DeleteIcon,
  RocketLaunch as RocketLaunchIcon, Send as SendIcon, Search as SearchIcon,
  Schedule as ScheduleIcon, People as PeopleIcon, Business as BusinessIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

const OBS_TYPE_COLORS = {
  flexible: 'default',
  time_based: 'primary',
  fixed_time: 'secondary',
  rise_to_set: 'success',
};

const AUTOMATION_COLORS = {
  active: 'success',
  paused: 'warning',
  completed: 'info',
  disabled: 'default',
};

const formatInterval = (days, hours) => {
  const totalHours = (days || 0) * 24 + (hours || 0);
  if (totalHours === 0) return 'No interval';
  if (totalHours < 1) return `Every ${Math.round(totalHours * 60)}m`;
  if (totalHours < 24) return `Every ${+totalHours.toFixed(1)}h`;
  const d = Math.floor(totalHours / 24);
  const h = +(totalHours % 24).toFixed(1);
  return h > 0 ? `Every ${d}d ${h}h` : `Every ${d}d`;
};

const formatDuration = (minutes) => {
  if (!minutes && minutes !== 0) return '-';
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hrs > 0) return `${hrs}h ${mins}m`;
  return `${mins} min`;
};

const getObservatoryName = (observatoryId, observatories) => {
  if (!observatoryId) return '-';
  // Handle populated object or plain ID
  if (typeof observatoryId === 'object' && observatoryId.name) return observatoryId.name;
  const obs = observatories.find(o => o._id === observatoryId);
  return obs ? obs.name : '-';
};

const getOwnerId = (template) => {
  if (typeof template.user_id === 'object' && template.user_id?._id) return template.user_id._id;
  return template.user_id;
};

const getOwnerName = (template) => {
  if (typeof template.user_id === 'object' && template.user_id?.username) return template.user_id.username;
  return null;
};

const TemplateTable = ({
  templates = [],
  projects = [],
  observatories = [],
  loading = false,
  currentUserId,
  onEdit,
  onDelete,
  onQuickSubmit,
  onEditAndSubmit,
  onRefresh,
  searchTerm = '',
  onSearchChange,
  filterProject = '',
  onFilterProjectChange,
  filterObservatory = '',
  onFilterObservatoryChange,
}) => {
  const [expandedProjects, setExpandedProjects] = useState({});

  // Build project lookup map
  const projectMap = useMemo(() => {
    const map = {};
    projects.forEach(p => {
      map[p._id] = p;
    });
    return map;
  }, [projects]);

  // Filter templates by search term and filters
  const filteredTemplates = useMemo(() => {
    return templates.filter(t => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const nameMatch = t.name?.toLowerCase().includes(term);
        if (!nameMatch) return false;
      }
      const tProjectId = t.project_id?._id || t.project_id;
      if (filterProject && tProjectId !== filterProject) {
        return false;
      }
      const tObsId = t.observatory_id?._id || t.observatory_id;
      if (filterObservatory && tObsId !== filterObservatory) {
        return false;
      }
      return true;
    });
  }, [templates, searchTerm, filterProject, filterObservatory]);

  // Group filtered templates by project_id
  const groupedTemplates = useMemo(() => {
    const grouped = {};
    filteredTemplates.forEach(template => {
      const projectId = template.project_id?._id || template.project_id || 'no-project';
      if (!grouped[projectId]) {
        grouped[projectId] = [];
      }
      grouped[projectId].push(template);
    });
    return grouped;
  }, [filteredTemplates]);

  const handleAccordionChange = (projectId) => (event, isExpanded) => {
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: isExpanded,
    }));
  };

  return (
    <Box>
      {/* Search and filter controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search templates..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Project</InputLabel>
          <Select
            value={filterProject}
            label="Project"
            onChange={(e) => onFilterProjectChange(e.target.value)}
          >
            <MenuItem value="">All Projects</MenuItem>
            {projects.map(p => (
              <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Observatory</InputLabel>
          <Select
            value={filterObservatory}
            label="Observatory"
            onChange={(e) => onFilterObservatoryChange(e.target.value)}
          >
            <MenuItem value="">All Observatories</MenuItem>
            {observatories.map(o => (
              <MenuItem key={o._id} value={o._id}>{o.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Chip label={`${filteredTemplates.length} template${filteredTemplates.length !== 1 ? 's' : ''}`} color="primary" />
      </Box>

      {/* Grouped accordion sections */}
      {Object.keys(groupedTemplates).length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="body1" color="text.secondary">
            {loading ? 'Loading templates...' : 'No templates found.'}
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {Object.entries(groupedTemplates).map(([projectId, projectTemplates]) => {
            const project = projectMap[projectId];
            return (
              <Accordion
                key={projectId}
                expanded={expandedProjects[projectId] ?? true}
                onChange={handleAccordionChange(projectId)}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                    <Typography sx={{ flexGrow: 1 }}>
                      {project ? project.name : 'No Project Assigned'}
                    </Typography>
                    <Chip
                      size="small"
                      label={`${projectTemplates.length} template${projectTemplates.length !== 1 ? 's' : ''}`}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Observatory</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Targets</TableCell>
                          <TableCell>Duration</TableCell>
                          <TableCell>Automation</TableCell>
                          <TableCell>Last Used</TableCell>
                          <TableCell>Observations</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {projectTemplates.map((template) => {
                          const isOwner = !currentUserId || getOwnerId(template) === currentUserId;
                          const ownerName = getOwnerName(template);
                          return (
                          <TableRow key={template._id} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2">{template.name}</Typography>
                                {template.visibility === 'observatory' && (
                                  <Tooltip title="Shared with observatory">
                                    <PeopleIcon fontSize="small" color="info" sx={{ fontSize: 16 }} />
                                  </Tooltip>
                                )}
                                {template.visibility === 'organization' && (
                                  <Tooltip title="Shared with organization">
                                    <BusinessIcon fontSize="small" color="secondary" sx={{ fontSize: 16 }} />
                                  </Tooltip>
                                )}
                              </Box>
                              {!isOwner && ownerName && (
                                <Typography variant="caption" color="text.secondary">
                                  by {ownerName}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {getObservatoryName(template.observatory_id, observatories)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={template.observation_type?.replace(/_/g, ' ') || 'flexible'}
                                size="small"
                                color={OBS_TYPE_COLORS[template.observation_type] || 'default'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              {template.targets?.length ?? 0}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <ScheduleIcon fontSize="small" color="action" />
                                <Typography variant="body2">
                                  {formatDuration(template.estimated_duration_minutes)}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box>
                                <Chip
                                  label={template.automation?.status || 'disabled'}
                                  size="small"
                                  color={AUTOMATION_COLORS[template.automation?.status] || 'default'}
                                />
                                {template.automation?.enabled && (
                                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                    {formatInterval(template.automation.interval_days, template.automation.interval_hours)}
                                    {template.automation.max_executions
                                      ? ` (${template.automation.executions_completed || 0}/${template.automation.max_executions})`
                                      : template.automation.executions_completed
                                        ? ` (${template.automation.executions_completed} runs)`
                                        : ''}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {template.last_used_at
                                  ? formatDistanceToNow(new Date(template.last_used_at), { addSuffix: true })
                                  : 'Never'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {template.observation_count ?? 0}
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Quick Submit">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => onQuickSubmit(template)}
                                >
                                  <RocketLaunchIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit & Submit">
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => onEditAndSubmit(template)}
                                >
                                  <SendIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={isOwner ? "Edit" : "Only the owner can edit"}>
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => onEdit(template)}
                                    disabled={!isOwner}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={isOwner ? "Delete" : "Only the owner can delete"}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => onDelete(template)}
                                    disabled={!isOwner}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default TemplateTable;
